import { Box, Button, Typography, IconButton, Paper, Collapse } from "@mui/material";
import { FieldArray } from "formik";
import DataInputForm from "./DataInputForm";
import { FileResponse, InputContent, StepCreateRequest, ValidationTypeResponse } from "api/main/data-contracts";
import { MetaInformation } from "../Process";
import { adminTheme } from "theme/adminTheme";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useState } from "react";

interface Props {
  meta?: MetaInformation;
  step: StepCreateRequest;
  stepIndex: number;
}

export default function DataInputDefinition(props: Props) {
  const { meta, step, stepIndex } = props;
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set(
    step?.stepInformation?.inputContent?.map((_, idx) => idx) ?? []
  ));

  function getSelectOptions(input: InputContent): string[] {
    let result;
    if (input.type === "FILE") {
      result = meta?.files?.map(
        (file: FileResponse) => `${file?.id}. ${file?.name}`,
      );
    }
    return result ?? [];
  }

  const toggleGroup = (groupIndex: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupIndex)) {
        newSet.delete(groupIndex);
      } else {
        newSet.add(groupIndex);
      }
      return newSet;
    });
  };

  return (
    <FieldArray
      name={`steps.${stepIndex}.stepInformation.inputContent`}
      render={(inputGroupArrayHelper) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Empty State */}
          {(!step?.stepInformation?.inputContent || step.stepInformation.inputContent.length === 0) && (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: adminTheme.colors.backgroundAlt,
                border: `2px dashed ${adminTheme.colors.border}`,
                borderRadius: adminTheme.borderRadius.md,
              }}
            >
              <FolderOpenIcon
                sx={{
                  fontSize: 48,
                  color: adminTheme.colors.textMuted,
                  mb: 2,
                  opacity: 0.5,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Nėra laukelių grupių. Pridėkite pirmąją grupę.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => inputGroupArrayHelper.push(getInputGroupInitialValues())}
                sx={{
                  background: adminTheme.gradients.primary,
                  "&:hover": { background: adminTheme.gradients.primaryHover },
                }}
              >
                Pridėti grupę
              </Button>
            </Paper>
          )}

          {/* Groups */}
          {step?.stepInformation?.inputContent?.map((group: InputContent[], inputGroupIndex: number) => (
            <Paper
              key={inputGroupIndex}
              elevation={0}
              sx={{
                border: `2px solid ${adminTheme.colors.primary}30`,
                borderRadius: adminTheme.borderRadius.lg,
                overflow: "hidden",
              }}
            >
              {/* Group Header */}
              <Box
                sx={{
                  p: 2,
                  background: `linear-gradient(135deg, ${adminTheme.colors.primary}12 0%, ${adminTheme.colors.primary}08 100%)`,
                  borderBottom: `1px solid ${adminTheme.colors.primary}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      background: adminTheme.gradients.primary,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      boxShadow: `0 2px 8px ${adminTheme.colors.primary}40`,
                    }}
                  >
                    {inputGroupIndex + 1}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, fontSize: "0.7rem" }}>
                      LAUKELIŲ GRUPĖ
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Grupė #{inputGroupIndex + 1}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => toggleGroup(inputGroupIndex)}
                    sx={{
                      bgcolor: adminTheme.colors.backgroundAlt,
                      "&:hover": { bgcolor: adminTheme.colors.border },
                    }}
                  >
                    {expandedGroups.has(inputGroupIndex) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (window.confirm(`Ar tikrai norite ištrinti grupę #${inputGroupIndex + 1}?`)) {
                        inputGroupArrayHelper.remove(inputGroupIndex);
                      }
                    }}
                    sx={{
                      color: adminTheme.colors.error,
                      bgcolor: `${adminTheme.colors.error}10`,
                      "&:hover": { bgcolor: `${adminTheme.colors.error}20` },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Group Content */}
              <Collapse in={expandedGroups.has(inputGroupIndex)}>
                <FieldArray
                  name={`steps.${stepIndex}.stepInformation.inputContent.${inputGroupIndex}`}
                  render={(inputHelper) => (
                    <Box sx={{ p: 2 }}>
                      {/* Fields */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                        {group?.length === 0 && (
                          <Paper
                            sx={{
                              p: 3,
                              textAlign: "center",
                              bgcolor: adminTheme.colors.backgroundAlt,
                              border: `1px dashed ${adminTheme.colors.border}`,
                              borderRadius: adminTheme.borderRadius.sm,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Nėra laukelių šioje grupėje
                            </Typography>
                          </Paper>
                        )}
                        {group?.map((input: InputContent, inputIndex: number) => (
                          <Paper
                            key={inputIndex}
                            elevation={0}
                            sx={{
                              p: 2.5,
                              bgcolor: adminTheme.colors.background,
                              border: `1px solid ${adminTheme.colors.border}`,
                              borderRadius: adminTheme.borderRadius.md,
                              transition: adminTheme.transitions.normal,
                              "&:hover": {
                                boxShadow: adminTheme.shadows.sm,
                                borderColor: adminTheme.colors.primary,
                              },
                            }}
                          >
                            {/* Field Header */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 2.5,
                                pb: 1.5,
                                borderBottom: `1px solid ${adminTheme.colors.border}`,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: `${adminTheme.colors.success}15`,
                                    color: adminTheme.colors.success,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {inputIndex + 1}
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, fontSize: "0.65rem" }}>
                                    LAUKELIS
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.85rem" }}>
                                    {input.label || `Laukelis #${inputIndex + 1}`}
                                  </Typography>
                                </Box>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (window.confirm(`Ar tikrai norite ištrinti laukelį "${input.label || inputIndex + 1}"?`)) {
                                    inputHelper.remove(inputIndex);
                                  }
                                }}
                                sx={{
                                  color: adminTheme.colors.error,
                                  "&:hover": { bgcolor: `${adminTheme.colors.error}10` },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            {/* Field Form */}
                            <DataInputForm
                              stepIndex={stepIndex}
                              inputGroupIndex={inputGroupIndex}
                              index={inputIndex}
                              selectOptions={getSelectOptions(input)}
                              inputTypes={meta?.inputTypes ?? []}
                              validatorTypes={getFilteredValidationTypes(
                                meta?.validatorTypes,
                                input.type,
                              )}
                            />
                          </Paper>
                        ))}
                      </Box>

                      {/* Add Field Button */}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          inputHelper.push(
                            getInputStepInitialValues(
                              undefined,
                              inputGroupIndex,
                              group?.length ?? 0,
                            ),
                          )
                        }
                        fullWidth
                        sx={{
                          borderColor: adminTheme.colors.success,
                          color: adminTheme.colors.success,
                          borderStyle: "dashed",
                          py: 1.5,
                          "&:hover": {
                            borderColor: adminTheme.colors.success,
                            bgcolor: `${adminTheme.colors.success}08`,
                            borderStyle: "dashed",
                          },
                        }}
                      >
                        Pridėti laukelį į grupę
                      </Button>
                    </Box>
                  )}
                />
              </Collapse>
            </Paper>
          ))}

          {/* Add Group Button */}
          {step?.stepInformation?.inputContent && step.stepInformation.inputContent.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => inputGroupArrayHelper.push(getInputGroupInitialValues())}
              fullWidth
              sx={{
                background: adminTheme.gradients.primary,
                py: 1.5,
                "&:hover": { background: adminTheme.gradients.primaryHover },
              }}
            >
              Pridėti naują grupę
            </Button>
          )}
        </Box>
      )}
    />
  );
}

function getFilteredValidationTypes(
  types?: ValidationTypeResponse[],
  inputType?: string,
): ValidationTypeResponse[] {
  return (
    types?.filter(
      (validation: ValidationTypeResponse) =>
        validation.inputType === inputType || !validation.inputType,
    ) ?? []
  );
}


function getInputGroupInitialValues(
  group?: InputContent[],
): InputContent[] {
  if (!group) return [];
  return group.map((input: InputContent) => getInputStepInitialValues(input));
}

function getInputStepInitialValues(
  inputStep?: InputContent,
  inputGroupIndex?: number,
  inputIndex?: number,
): InputContent {
  
  return {
    name: inputStep?.name ?? `group${inputGroupIndex}field${inputIndex}`,
    label: inputStep?.label ?? "",
    i18nkey: inputStep?.i18nkey ?? "",
    type: inputStep?.type,
    options: inputStep?.options ?? [],
    selectOptions: inputStep?.selectOptions ?? [],
    validations: inputStep?.validations ?? [],
  };
}
